<template>
  <div class="application-status-pane">
    <div class="status-rows">
      <div class="status-row">
        <div class="status-label">Applied</div>
        <div class="status-input">
          <button type="button" class="yes-toggle is-on is-locked" disabled :aria-pressed="'true'" :aria-disabled="'true'" title="Applied status cannot be changed">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">YES</span>
          </button>
        </div>
        <div class="date-box has-date">{{ stageDate(applied, appliedOn) }}</div>
      </div>

      <div class="status-row">
        <div class="status-label">Reviewed</div>
        <div class="status-input">
          <button type="button" class="yes-toggle" :class="{ 'is-on': reviewed, 'is-locked': isReviewedLocked }" :disabled="isReviewedLocked" :aria-pressed="reviewed ? 'true' : 'false'" :aria-disabled="isReviewedLocked ? 'true' : 'false'" :title="isReviewedLocked ? 'Reviewed status cannot be changed' : ''" @click="onReviewedInput(!reviewed)">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">{{ reviewed ? 'YES' : 'NO' }}</span>
          </button>
        </div>
        <div class="date-cell">
          <div class="date-box" v-if="!reviewed">N/A</div>
          <div class="date-box has-date" v-else-if="isReviewedLocked">{{ formatLockedDate(reviewedDate) }}</div>
          <datepicker
            v-else
            v-model="reviewedDate"
            :open-date="reviewedDate || appliedDate"
            :format="dateFormat"
            wrapper-class="status-datepicker"
            input-class="status-datepicker-input"
            :clear-button="false"
            :disabledDates="reviewedDisabledDates"
            @selected="clampLaterFrom('reviewedDate', $event)"
            @opened="onDatepickerOpened('reviewedDate')">
          </datepicker>
        </div>
      </div>

      <div class="status-row">
        <div class="status-label">Interview Scheduled</div>
        <div class="status-input">
          <button type="button" class="yes-toggle" :class="{ 'is-on': interviewScheduled, 'is-locked': isInterviewScheduledLocked }" :disabled="isInterviewScheduledLocked" :aria-pressed="interviewScheduled ? 'true' : 'false'" :aria-disabled="isInterviewScheduledLocked ? 'true' : 'false'" :title="isInterviewScheduledLocked ? 'Interview Scheduled status cannot be changed' : ''" @click="onInterviewScheduledInput(!interviewScheduled)">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">{{ interviewScheduled ? 'YES' : 'NO' }}</span>
          </button>
        </div>
        <div class="date-cell">
          <div class="date-box" v-if="!interviewScheduled">N/A</div>
          <div class="date-box has-date" v-else-if="isInterviewScheduledLocked">{{ formatLockedDate(interviewScheduledDate) }}</div>
          <datepicker
            v-else
            v-model="interviewScheduledDate"
            :open-date="interviewScheduledDate || reviewedDate || appliedDate"
            :format="dateFormat"
            wrapper-class="status-datepicker"
            input-class="status-datepicker-input"
            :clear-button="false"
            :disabledDates="interviewScheduledDisabledDates"
            @selected="clampLaterFrom('interviewScheduledDate', $event)"
            @opened="onDatepickerOpened('interviewScheduledDate')">
          </datepicker>
        </div>
      </div>

      <div class="status-row">
        <div class="status-label">Interviewed</div>
        <div class="status-input">
          <button type="button" class="yes-toggle" :class="{ 'is-on': interviewed, 'is-locked': isInterviewedLocked }" :disabled="isInterviewedLocked" :aria-pressed="interviewed ? 'true' : 'false'" :aria-disabled="isInterviewedLocked ? 'true' : 'false'" :title="isInterviewedLocked ? 'Interviewed status cannot be changed' : ''" @click="onInterviewedInput(!interviewed)">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">{{ interviewed ? 'YES' : 'NO' }}</span>
          </button>
        </div>
        <div class="date-cell">
          <div class="date-box" v-if="!interviewed">N/A</div>
          <div class="date-box has-date" v-else-if="isInterviewedLocked">{{ formatLockedDate(interviewedDate) }}</div>
          <datepicker
            v-else
            v-model="interviewedDate"
            :open-date="interviewedDate || interviewScheduledDate || reviewedDate || appliedDate"
            :format="dateFormat"
            wrapper-class="status-datepicker"
            input-class="status-datepicker-input"
            :clear-button="false"
            :disabledDates="interviewedDisabledDates"
            @selected="clampLaterFrom('interviewedDate', $event)"
            @opened="onDatepickerOpened('interviewedDate')">
          </datepicker>
        </div>
      </div>

      <div class="status-row">
        <div class="status-label">Job Offer</div>
        <div class="status-input">
          <button type="button" class="yes-toggle" :class="{ 'is-on': offered, 'is-locked': isOfferedLocked }" :disabled="isOfferedLocked" :aria-pressed="offered ? 'true' : 'false'" :aria-disabled="isOfferedLocked ? 'true' : 'false'" :title="isOfferedLocked ? 'Job Offer status cannot be changed' : ''" @click="onOfferedInput(!offered)">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">{{ offered ? 'YES' : 'NO' }}</span>
          </button>
        </div>
        <div class="date-cell">
          <div class="date-box" v-if="!offered">N/A</div>
          <div class="date-box has-date" v-else-if="isOfferedLocked">{{ formatLockedDate(offeredDate) }}</div>
          <datepicker
            v-else
            v-model="offeredDate"
            :open-date="offeredDate || interviewedDate || interviewScheduledDate || reviewedDate || appliedDate"
            :format="dateFormat"
            wrapper-class="status-datepicker"
            input-class="status-datepicker-input"
            :clear-button="false"
            :disabledDates="offeredDisabledDates"
            @selected="clampLaterFrom('offeredDate', $event)"
            @opened="onDatepickerOpened('offeredDate')">
          </datepicker>
        </div>
      </div>

      <div class="status-row">
        <div class="status-label">Job Acceptance</div>
        <div class="status-input">
          <button
            v-if="accepted"
            type="button"
            class="yes-toggle is-on is-locked"
            disabled
            aria-pressed="true"
            aria-disabled="true"
            title="Job Acceptance status cannot be changed">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">YES</span>
          </button>
          <button
            v-else-if="isDeclined"
            type="button"
            class="yes-toggle is-locked is-declined"
            disabled
            aria-pressed="false"
            aria-disabled="true"
            title="Job Acceptance status cannot be changed">
            <span class="yes-toggle-knob"></span>
            <span class="yes-toggle-text">NO</span>
          </button>
          <div v-else-if="offered" class="pending-status">Pending</div>
          <div v-else class="na-status">N/A</div>
        </div>
        <div class="date-cell">
          <div class="date-box" v-if="!accepted && !isDeclined">N/A</div>
          <div class="date-box has-date is-declined-date" v-else-if="isDeclined">{{ formatLockedDate(declinedDate) }}</div>
          <div class="date-box has-date" v-else>{{ formatLockedDate(acceptedDate) }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapActions } from 'vuex'
  import { DateTime } from 'luxon'
  import Datepicker from 'vuejs-datepicker'

  var DisableDate = function (startDate) {
    const date = (startDate) ? new Date(startDate) : new Date()
    return new Date(date.getFullYear(), date.getMonth(), date.getDate())
  }
  export default {
    name: 'jobApplicationStatus',
    components: {
      Datepicker
    },
    props: {
      application: {
        type: Object,
        default: function () {
          return {}
        }
      }
    },
    data: () => {
      return {
        applied: true,
        reviewed: false,
        interviewScheduled: false,
        interviewed: false,
        offered: false,
        accepted: false,
        reviewedDate: null,
        interviewScheduledDate: null,
        interviewedDate: null,
        offeredDate: null,
        acceptedDate: null,
        declinedDate: null,
        dateFormat: 'MM/dd/yyyy'
      }
    },
    created () {
      this.hydrateFromApplication()
    },
    methods: {
      ...mapActions([
        'updateJobApplication'
      ]),
      hydrateFromApplication () {
        let app = this.application || {}
        let declined = this.isDeclinedApplication(app)
        let rank = this.statusRank(app.status)
        this.applied = true
        this.reviewed = rank >= this.statusRank('Reviewed') || !!app.reviewedOn
        this.interviewScheduled = rank >= this.statusRank('Interview Scheduled') || !!app.interviewScheduledOn
        this.interviewed = rank >= this.statusRank('Interview Done') || !!app.interviewedOn
        this.offered = rank >= this.statusRank('Job Offered') || !!app.offeredOn || !!app.acceptedOn || declined
        this.accepted = !declined && (rank >= this.statusRank('Job Accepted') || !!app.acceptedOn || app.status === 'Accepted')
        if ((app.interviewRejected || app.interviewScheduleStatus === 'Rejected') && !this.interviewed) {
          this.interviewScheduled = false
        }
        this.reviewedDate = this.reviewed ? this.toDate(app.reviewedOn) : null
        this.interviewScheduledDate = this.interviewScheduled ? this.toDate(app.interviewScheduledOn) : null
        this.interviewedDate = this.interviewed ? this.toDate(app.interviewedOn) : null
        this.offeredDate = this.offered ? this.toDate(app.offeredOn) : null
        this.acceptedDate = this.accepted ? this.toDate(app.acceptedOn) : null
        this.declinedDate = declined ? this.toDate(app.declinedOn) : null
        if (this.reviewed) {
          this.ensureDate('reviewedDate')
        }
        if (this.interviewScheduled) {
          this.ensureDate('interviewScheduledDate')
        }
        if (this.interviewed) {
          this.ensureDate('interviewedDate')
        }
        if (this.offered) {
          this.ensureDate('offeredDate')
        }
        if (this.accepted) {
          this.ensureDate('acceptedDate')
        }
        if (declined) {
          this.ensureDate('declinedDate')
        }
      },
      isDeclinedApplication (app) {
        if (!app) {
          return false
        }
        return !!(app.status === 'Declined' || app.declinedOn)
      },
      statusRank (status) {
        if (status === 'Accepted') {
          status = 'Job Accepted'
        }
        if (status === 'Declined') {
          status = 'Job Offered'
        }
        let order = ['Applied', 'Reviewed', 'Interview Scheduled', 'Interview Done', 'Job Offered', 'Job Accepted']
        let index = order.indexOf(status)
        return index < 0 ? 0 : index
      },
      onReviewedInput (val) {
        if (this.isReviewedLocked) {
          return
        }
        this.reviewed = val
        if (val) {
          this.applied = true
          this.ensureDate('reviewedDate')
        } else {
          this.clearFrom('reviewed')
        }
      },
      onInterviewScheduledInput (val) {
        if (this.isInterviewScheduledLocked) {
          return
        }
        this.interviewScheduled = val
        if (val) {
          this.applied = true
          if (!this.isReviewedLocked) {
            this.reviewed = true
            this.ensureDate('reviewedDate')
          }
          this.ensureDate('interviewScheduledDate')
        } else {
          this.clearFrom('interviewScheduled')
        }
      },
      onInterviewedInput (val) {
        if (this.isInterviewedLocked) {
          return
        }
        this.interviewed = val
        if (val) {
          this.applied = true
          if (!this.isReviewedLocked) {
            this.reviewed = true
            this.ensureDate('reviewedDate')
          }
          if (!this.isInterviewScheduledLocked) {
            this.interviewScheduled = true
            this.ensureDate('interviewScheduledDate')
          }
          this.ensureDate('interviewedDate')
        } else {
          if (!this.isOfferedLocked) {
            this.offered = false
            this.offeredDate = null
          }
          if (!this.isAcceptedLocked) {
            this.accepted = false
            this.acceptedDate = null
          }
        }
      },
      onOfferedInput (val) {
        if (this.isOfferedLocked) {
          return
        }
        this.offered = val
        if (val) {
          this.applied = true
          if (!this.isReviewedLocked) {
            this.reviewed = true
            this.ensureDate('reviewedDate')
          }
          if (!this.isInterviewScheduledLocked) {
            this.interviewScheduled = true
            this.ensureDate('interviewScheduledDate')
          }
          if (!this.isInterviewedLocked) {
            this.interviewed = true
            this.ensureDate('interviewedDate')
          }
          this.ensureDate('offeredDate')
        } else {
          if (!this.isAcceptedLocked) {
            this.accepted = false
            this.acceptedDate = null
          }
        }
      },
      onAcceptedInput (val) {
        return
      },
      clearFrom (stage) {
        if (stage === 'reviewed' || stage === 'interviewScheduled' || stage === 'interviewed') {
          if (stage === 'reviewed' && !this.isReviewedLocked) {
            this.reviewed = false
            this.reviewedDate = null
          }
          if ((stage === 'reviewed' || stage === 'interviewScheduled') && !this.isInterviewScheduledLocked) {
            this.interviewScheduled = false
            this.interviewScheduledDate = null
          }
          if (!this.isInterviewedLocked) {
            this.interviewed = false
            this.interviewedDate = null
          }
          if (!this.isOfferedLocked) {
            this.offered = false
            this.offeredDate = null
          }
          if (!this.isAcceptedLocked) {
            this.accepted = false
            this.acceptedDate = null
          }
        }
      },
      formatLockedDate (date) {
        if (this.isValidDate(date)) {
          return DateTime.fromJSDate(date).toFormat('MM/dd/yyyy')
        }
        return this.todayLabel()
      },
      startOfDay (date) {
        let value = date instanceof Date ? date : this.toDate(date)
        if (!this.isValidDate(value)) {
          return null
        }
        return new Date(value.getFullYear(), value.getMonth(), value.getDate())
      },
      minDateForField (field) {
        if (field === 'reviewedDate') {
          return this.appliedDate
        }
        if (field === 'interviewScheduledDate') {
          return this.startOfDay(this.reviewedDate) || this.appliedDate
        }
        if (field === 'interviewedDate') {
          return this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate
        }
        if (field === 'offeredDate') {
          return this.startOfDay(this.interviewedDate) || this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate
        }
        if (field === 'acceptedDate') {
          return this.startOfDay(this.offeredDate) || this.startOfDay(this.interviewedDate) || this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate
        }
        return this.todayDate()
      },
      disabledDatesFrom (date) {
        return {
          to: new DisableDate(date || this.todayDate())
        }
      },
      isDateFieldLocked (field) {
        if (field === 'reviewedDate') {
          return this.isReviewedLocked
        }
        if (field === 'interviewScheduledDate') {
          return this.isInterviewScheduledLocked
        }
        if (field === 'interviewedDate') {
          return this.isInterviewedLocked
        }
        if (field === 'offeredDate') {
          return this.isOfferedLocked
        }
        if (field === 'acceptedDate') {
          return this.isAcceptedLocked
        }
        return false
      },
      clampLaterFrom (field, selectedDate) {
        let fields = ['reviewedDate', 'interviewScheduledDate', 'interviewedDate', 'offeredDate', 'acceptedDate']
        let start = fields.indexOf(field)
        if (start < 0) {
          return
        }
        let min = this.startOfDay(selectedDate) || this.startOfDay(this[field])
        for (let i = start + 1; i < fields.length; i++) {
          let next = fields[i]
          if (this.isDateFieldLocked(next) || !this.isValidDate(this[next])) {
            if (this.isValidDate(this[next])) {
              min = this.startOfDay(this[next]) || min
            }
            continue
          }
          if (min && this.startOfDay(this[next]).getTime() < min.getTime()) {
            this[next] = new Date(min.getTime())
          }
          min = this.startOfDay(this[next]) || min
        }
      },
      ensureDate (field) {
        let min = this.minDateForField(field)
        if (!this.isValidDate(this[field]) || (min && this.startOfDay(this[field]).getTime() < min.getTime())) {
          this[field] = min ? new Date(min.getTime()) : this.todayDate()
        }
      },
      onDatepickerOpened (field) {
        this.ensureDate(field)
      },
      todayDate () {
        let now = new Date()
        return new Date(now.getFullYear(), now.getMonth(), now.getDate())
      },
      hasValue (value) {
        return value !== null && value !== undefined && value !== '' && value !== 0 && value !== '0'
      },
      isValidDate (date) {
        if (!(date instanceof Date) || isNaN(date.getTime())) {
          return false
        }
        return date.getFullYear() >= 1971
      },
      toDate (value) {
        if (!this.hasValue(value)) {
          return null
        }
        let date = null
        if (value instanceof Date) {
          date = value
        } else if (typeof value === 'number') {
          date = new Date(value)
        } else if (typeof value === 'string') {
          let parsed = DateTime.fromISO(value)
          if (!parsed.isValid) {
            parsed = DateTime.fromMillis(Number(value))
          }
          date = parsed.isValid ? parsed.toJSDate() : null
        }
        return this.isValidDate(date) ? date : null
      },
      dateToMillis (value) {
        let date = this.toDate(value)
        return date ? date.getTime() : null
      },
      stageDate (enabled, value) {
        if (!enabled) {
          return 'N/A'
        }
        if (this.hasValue(value)) {
          return this.millisToDate(value)
        }
        return this.todayLabel()
      },
      todayLabel () {
        return DateTime.local().toFormat('MM/dd/yyyy')
      },
      millisToDate (millis) {
        if (!millis) {
          return 'N/A'
        }
        if (typeof millis === 'string') {
          let parsed = DateTime.fromISO(millis)
          if (!parsed.isValid) {
            parsed = DateTime.fromMillis(Number(millis))
          }
          return parsed.isValid ? parsed.toFormat('MM/dd/yyyy') : 'N/A'
        }
        return DateTime.fromMillis(millis).toFormat('MM/dd/yyyy')
      },
      applicationUuid () {
        let application = this.application
        if (!application) {
          return ''
        }
        return application.uuid || application.applicationUuid || application.applicationId || ''
      },
      derivedStatus () {
        if (this.isDeclined) {
          return 'Declined'
        }
        if (this.accepted) {
          return 'Job Accepted'
        }
        if (this.offered) {
          return 'Job Offered'
        }
        if (this.interviewed) {
          return 'Interview Done'
        }
        if (this.interviewScheduled) {
          return 'Interview Scheduled'
        }
        if (this.reviewed) {
          return 'Reviewed'
        }
        return 'Applied'
      },
      saveStatus () {
        if (!this.application || !this.applicationUuid()) {
          return
        }
        let jobApplication = Object.assign({}, this.application)
        jobApplication.status = this.derivedStatus()
        jobApplication.reviewedOn = this.reviewed ? this.dateToMillis(this.reviewedDate) : null
        jobApplication.interviewScheduledOn = this.interviewScheduled ? this.dateToMillis(this.interviewScheduledDate) : null
        jobApplication.interviewedOn = this.interviewed ? this.dateToMillis(this.interviewedDate) : null
        jobApplication.offeredOn = this.offered ? this.dateToMillis(this.offeredDate) : null
        jobApplication.acceptedOn = this.accepted ? this.dateToMillis(this.acceptedDate) : null
        this.updateJobApplication(jobApplication).then((response) => {
          let applicantName = jobApplication.userFullName || jobApplication.userName || ''
          let message = (response ? 'Successfully updated application status' : 'Unable to update application status')
          if (applicantName) {
            message = message + ' for \'' + applicantName + '\''
          }
          message = message + ' to \'' + jobApplication.status + '\''
          if (response) {
            this.notifySuccess(message)
            this.$emit('saved')
          } else {
            this.notifyFailure(message)
          }
        })
      },
      notifySuccess (text) {
        return this.$notify({
          group: 'notify',
          type: 'success',
          title: '',
          text: text
        })
      },
      notifyFailure (text) {
        return this.$notify({
          group: 'notify',
          type: 'error',
          title: '',
          text: text
        })
      }
    },
    computed: {
      appliedDate () {
        return this.startOfDay(this.appliedOn) || this.todayDate()
      },
      reviewedDisabledDates () {
        return this.disabledDatesFrom(this.appliedDate)
      },
      interviewScheduledDisabledDates () {
        return this.disabledDatesFrom(this.startOfDay(this.reviewedDate) || this.appliedDate)
      },
      interviewedDisabledDates () {
        return this.disabledDatesFrom(this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate)
      },
      offeredDisabledDates () {
        return this.disabledDatesFrom(this.startOfDay(this.interviewedDate) || this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate)
      },
      acceptedDisabledDates () {
        return this.disabledDatesFrom(this.startOfDay(this.offeredDate) || this.startOfDay(this.interviewedDate) || this.startOfDay(this.interviewScheduledDate) || this.startOfDay(this.reviewedDate) || this.appliedDate)
      },
      savedStatusRank () {
        return this.statusRank(this.application && this.application.status)
      },
      isReviewedLocked () {
        return this.savedStatusRank >= this.statusRank('Reviewed')
      },
      isInterviewScheduledLocked () {
        return this.savedStatusRank >= this.statusRank('Interview Scheduled')
      },
      isInterviewedLocked () {
        return this.savedStatusRank >= this.statusRank('Interview Done')
      },
      isOfferedLocked () {
        return this.savedStatusRank >= this.statusRank('Job Offered') || this.isDeclined
      },
      isAcceptedLocked () {
        return true
      },
      isDeclined () {
        return this.isDeclinedApplication(this.application)
      },
      appliedOn () {
        return this.application && (this.application.appliedOn || this.application.creationTime || this.application.appliedTime)
      }
    },
    watch: {
      application () {
        this.hydrateFromApplication()
      },
      reviewedDate () {
        this.clampLaterFrom('reviewedDate')
      },
      interviewScheduledDate () {
        this.clampLaterFrom('interviewScheduledDate')
      },
      interviewedDate () {
        this.clampLaterFrom('interviewedDate')
      },
      offeredDate () {
        this.clampLaterFrom('offeredDate')
      }
    }
  }
</script>

<style scoped>
  .application-status-pane {
    padding: 0px 24px 240px;
    min-height: 100%;
    overflow: visible;
  }

  .status-row {
    display: grid;
    grid-template-columns: 180px 170px 110px;
    align-items: center;
    justify-content: center;
    column-gap: 18px;
    margin: 0 0 16px;
    max-width: 510px;
  }

  .status-label {
    text-align: left;
    font-size: 15px;
    padding-top: 6px;
  }

  .status-input {
    display: flex;
    align-items: center;
    min-height: 32px;
  }

  .pending-status,
  .na-status {
    width: 70px;
    height: 28px;
    min-height: 28px;
    border-radius: 14px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    line-height: 1;
  }

  .pending-status {
    background: #f6e59b;
  }

  .na-status {
    background: #dcdcdc;
  }

  .date-cell {
    width: 110px;
    min-width: 110px;
    position: relative;
    z-index: 1;
  }

  .date-cell:hover,
  .date-cell:focus-within {
    z-index: 5;
  }

  .date-box {
    width: 110px;
    min-width: 110px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 3px;
    background: #dcdcdc;
    font-size: 14px;
    box-sizing: border-box;
  }

  .date-box.has-date {
    background: #c8e6c0;
  }

  .date-box.has-date.is-declined-date {
    background: #c97a7a;
  }

  .status-datepicker {
    width: 110px;
    display: block;
    position: relative;
  }

  .application-status-pane ::v-deep .vdp-datepicker {
    width: 110px;
    display: block;
    position: relative;
  }

  .application-status-pane ::v-deep .status-datepicker::after,
  .application-status-pane ::v-deep .vdp-datepicker.status-datepicker::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 7px;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #555;
    pointer-events: none;
    z-index: 2;
  }

  .application-status-pane ::v-deep .vdp-datepicker input,
  .application-status-pane ::v-deep .status-datepicker-input {
    width: 110px !important;
    max-width: 110px !important;
    min-width: 0 !important;
    height: 32px !important;
    min-height: 32px !important;
    padding: 0 16px 0 4px !important;
    border: 1px solid #c8c8c8 !important;
    border-radius: 3px;
    background: #f6e59b !important;
    font-size: 14px !important;
    line-height: 30px !important;
    text-align: center;
    color: #333 !important;
    box-sizing: border-box !important;
    cursor: pointer;
  }

  .application-status-pane ::v-deep .vdp-datepicker__calendar {
    z-index: 30;
  }

  .yes-toggle {
    position: relative;
    width: 70px;
    height: 28px;
    min-height: 28px;
    border: none;
    border-radius: 14px;
    background: #9a9a9a;
    padding: 0;
    cursor: pointer;
    overflow: hidden;
    display: inline-block;
    vertical-align: middle;
    box-shadow: none;
  }

  .yes-toggle:hover,
  .yes-toggle:focus,
  .yes-toggle:active {
    background: #9a9a9a;
    color: #000;
    box-shadow: none;
    outline: none;
  }

  .yes-toggle.is-on:not(.is-locked),
  .yes-toggle.is-on:not(.is-locked):hover,
  .yes-toggle.is-on:not(.is-locked):focus,
  .yes-toggle.is-on:not(.is-locked):active {
    background: #f6e59b;
    color: #000;
    box-shadow: none;
  }

  .yes-toggle.is-on.is-locked,
  .yes-toggle.is-on.is-locked:hover,
  .yes-toggle.is-on.is-locked:focus,
  .yes-toggle.is-on.is-locked:active,
  .yes-toggle.is-on.is-locked:disabled {
    background: rgb(21, 115, 25) !important;
    color: #000 !important;
    cursor: default;
    opacity: 1 !important;
    box-shadow: none;
  }

  .yes-toggle.is-declined.is-locked,
  .yes-toggle.is-declined.is-locked:hover,
  .yes-toggle.is-declined.is-locked:focus,
  .yes-toggle.is-declined.is-locked:active,
  .yes-toggle.is-declined.is-locked:disabled {
    background: #c97a7a !important;
    color: #000 !important;
    cursor: default;
    opacity: 1 !important;
    box-shadow: none;
  }

  .yes-toggle.is-locked,
  .yes-toggle.is-locked:hover,
  .yes-toggle.is-locked:focus,
  .yes-toggle.is-locked:active,
  .yes-toggle:disabled {
    cursor: default;
    opacity: 1;
  }

  .yes-toggle-knob {
    position: absolute;
    top: 3px;
    left: 45px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.18);
  }

  .yes-toggle.is-on .yes-toggle-knob {
    left: 3px;
  }

  .yes-toggle-text {
    position: relative;
    z-index: 1;
    display: block;
    width: 100%;
    line-height: 28px;
    color: #000;
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.3px;
    text-transform: uppercase;
  }

  .yes-toggle .yes-toggle-text,
  .yes-toggle:hover .yes-toggle-text,
  .yes-toggle:focus .yes-toggle-text,
  .yes-toggle.is-on .yes-toggle-text,
  .yes-toggle.is-locked .yes-toggle-text,
  .yes-toggle:disabled .yes-toggle-text {
    color: #000 !important;
  }

  .yes-toggle.is-on .yes-toggle-text {
    padding-left: 18px;
    text-align: center;
  }

  .yes-toggle:not(.is-on) .yes-toggle-text {
    padding-right: 18px;
    text-align: center;
  }
</style>

<style>
  .application-status-pane .vdp-datepicker {
    width: 110px;
    display: block;
    position: relative;
  }

  .application-status-pane .vdp-datepicker.status-datepicker::after {
    content: '';
    position: absolute;
    top: 50%;
    right: 7px;
    width: 0;
    height: 0;
    margin-top: -2px;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 5px solid #555;
    pointer-events: none;
    z-index: 2;
  }

  .application-status-pane .vdp-datepicker input {
    width: 110px !important;
    max-width: 110px !important;
    min-width: 0 !important;
    height: 32px !important;
    padding: 0 16px 0 4px !important;
    box-sizing: border-box !important;
    background: #f6e59b !important;
    color: #333 !important;
    font-size: 14px !important;
    text-align: center;
    cursor: pointer;
  }
</style>



// WEBPACK FOOTER //
// src/app/job/components/JobApplicationStatus.vue